import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import {
  PresetAvailableTimeRow,
  PresetResponse,
  presetsApi,
} from "../../lib/presets";
import { getAreaPresetTypeLabel } from "../../lib/areas";
import type { CurfewConfig } from "../../lib/weekly-availability";
import { useAuth } from "../../hooks/useAuth";
import LightPresetAvailabilityCell from "../scheduling/LightPresetAvailabilityCell";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface PresetsListProps {
  /** When `light`, an availability column is shown for presets. */
  areaType: "user" | "light" | "hidden" | "system";
  /** Area lighting curfew (always pass for light areas). */
  areaCurfew: CurfewConfig;
  /** Site lighting curfew; omit if unknown (comparison uses area-only). */
  siteCurfew?: CurfewConfig;
  presets: PresetResponse[];
  onEdit: (preset: PresetResponse) => void;
  onDelete?: (id: string) => void;
}

export default function PresetsList({
  areaType,
  areaCurfew,
  siteCurfew,
  presets,
  onEdit,
  onDelete,
}: PresetsListProps) {
  const { user } = useAuth();
  const canLoadSchedule =
    user?.role === "super_user" || user?.role === "site_admin";

  const lightPresetIds = useMemo(
    () => presets.filter((p) => p.type === "light").map((p) => p.id),
    [presets],
  );

  const availabilityQueries = useQueries({
    queries: lightPresetIds.map((id) => ({
      queryKey: ["preset-available-times", id] as const,
      queryFn: () => presetsApi.getAvailableTimes(id),
      enabled:
        areaType === "light" && canLoadSchedule && lightPresetIds.length > 0,
      staleTime: 60_000,
    })),
  });

  const availabilityByPresetId = useMemo(() => {
    const m = new Map<
      string,
      | { state: "ok"; rows: PresetAvailableTimeRow[] }
      | { state: "pending" }
      | { state: "error" }
    >();
    if (areaType !== "light" || !canLoadSchedule) return m;
    lightPresetIds.forEach((id, i) => {
      const q = availabilityQueries[i];
      if (!q) return;
      if (q.isPending) m.set(id, { state: "pending" });
      else if (q.isError) m.set(id, { state: "error" });
      else m.set(id, { state: "ok", rows: q.data ?? [] });
    });
    return m;
  }, [areaType, canLoadSchedule, lightPresetIds, availabilityQueries]);

  const handleRowClick = (preset: PresetResponse) => {
    onEdit(preset);
  };

  if (presets.length === 0) {
    return (
      <Card className="text-center py-12">
        <CardContent>
          <p className="text-muted-foreground">No presets available</p>
        </CardContent>
      </Card>
    );
  }

  const showAvailabilityCol = areaType === "light";

  return (
    <Card>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="px-3 sm:px-4 md:px-6">Name</TableHead>
              <TableHead className="px-3 sm:px-4 md:px-6">Address</TableHead>
              <TableHead className="px-3 sm:px-4 md:px-6">Type</TableHead>
              {showAvailabilityCol && (
                <TableHead className="hidden md:table-cell px-3 sm:px-4 md:px-6 min-w-[8rem]">
                  Availability
                </TableHead>
              )}
              {presets[0]?.type === "user" && (
                <TableHead className="hidden sm:table-cell px-3 sm:px-4 md:px-6">
                  Group
                </TableHead>
              )}
              <TableHead className="text-right px-3 sm:px-4 md:px-6">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {presets
              .sort((a, b) =>
                a.address !== null && a.address !== undefined
                  ? a.address
                  : 0 -
                    (b.address !== null && b.address !== undefined
                      ? b.address
                      : 0),
              )
              .map((preset) => {
                const avail =
                  showAvailabilityCol && preset.type === "light"
                    ? availabilityByPresetId.get(preset.id)
                    : undefined;

                return (
                  <TableRow
                    key={preset.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(preset)}
                  >
                    <TableCell className="px-3 sm:px-4 md:px-6 max-w-0">
                      <div className="truncate" title={preset.name}>
                        {preset.name}
                      </div>
                    </TableCell>
                    <TableCell className="px-3 sm:px-4 md:px-6">
                      {preset.address !== null && preset.address !== undefined
                        ? preset.address
                        : "-"}
                    </TableCell>
                    <TableCell className="px-3 sm:px-4 md:px-6">
                      {getAreaPresetTypeLabel(preset.type)}
                    </TableCell>
                    {showAvailabilityCol && (
                      <TableCell className="hidden md:table-cell px-3 sm:px-4 md:px-6 text-xs text-muted-foreground">
                        {preset.type !== "light" ? (
                          "—"
                        ) : (
                          <LightPresetAvailabilityCell
                            canLoadSchedule={canLoadSchedule}
                            availability={
                              avail?.state === "pending"
                                ? { state: "pending" }
                                : avail?.state === "error"
                                  ? { state: "error" }
                                  : avail?.state === "ok"
                                    ? { state: "ok", rows: avail.rows }
                                    : { state: "pending" }
                            }
                            siteCurfew={
                              siteCurfew ?? {
                                start: undefined,
                                end: undefined,
                              }
                            }
                            areaCurfew={areaCurfew}
                          />
                        )}
                      </TableCell>
                    )}
                    {preset.type === "user" && (
                      <TableCell className="hidden sm:table-cell px-3 sm:px-4 md:px-6">
                        <div
                          className="truncate"
                          title={preset.userGroupName ?? ""}
                        >
                          {preset.userGroupName}
                        </div>
                      </TableCell>
                    )}
                    <TableCell className="px-3 sm:px-4 md:px-6 whitespace-nowrap text-right">
                      <div
                        className="flex justify-end gap-2 sm:gap-3 flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {onDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDelete(preset.id)}
                            className="text-destructive hover:text-destructive"
                            title="Delete preset"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="hidden sm:inline ml-1">
                              Delete
                            </span>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
